"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ProductCard } from "./ProductCard";
import { ShowcaseProductCard } from "@/components/Organisms/ProductsGallerySectionBrand/ShowcaseProductCard";
import { LimitedEditionRelatedCard } from "@/components/Organisms/LimitedEditionSection/LimitedEditionRelatedCard";
import {
	NextButton,
	PrevButton,
	usePrevNextButtons,
} from "./embla/EmblaCarouselArrowButtons";
import { cn } from "@/lib/functions";
import type { ButtonMasterVariantProps } from "@/components/Atoms/Buttons";
import styles from "./related-embla.module.scss";

type RelatedProductsCarouselProps = {
	slides: any[];
	loop: boolean;
	showNav: boolean;
	data: any;
	navColor: { hex?: string } | undefined;
	navStyle: React.CSSProperties;
	locale: string | undefined;
	portfolioSlug: string;
	buttonConfig?: any;
	cardVariant?: "default" | "showcase" | "limitedEdition";
	carouselImageSize?: "default" | "related";
	carouselLayout?: "default" | "gallery";
	centerFocusZoom?: boolean;
	showcaseTextColor?: { hex?: string; cssRgb?: string };
	showcaseButtonStyle?: ButtonMasterVariantProps["type"];
	showcaseButtonConfig?: any;
	showBrandLabel?: boolean;
	brandLabelOverride?: string;
	limitedEditionButtonConfig?: any;
};

type GalleryColumns = 1 | 2 | 3;

function useGalleryColumns(enabled: boolean): GalleryColumns {
	const [columns, setColumns] = useState<GalleryColumns>(1);

	useEffect(() => {
		if (!enabled) return;

		const mqThree = window.matchMedia("(min-width: 1280px)");
		const mqTwo = window.matchMedia("(min-width: 1024px)");

		const update = () => {
			if (mqThree.matches) setColumns(3);
			else if (mqTwo.matches) setColumns(2);
			else setColumns(1);
		};

		update();
		mqThree.addEventListener("change", update);
		mqTwo.addEventListener("change", update);

		return () => {
			mqThree.removeEventListener("change", update);
			mqTwo.removeEventListener("change", update);
		};
	}, [enabled]);

	return columns;
}

export function RelatedProductsCarousel({
	slides,
	loop,
	showNav,
	data,
	navColor,
	navStyle,
	locale,
	portfolioSlug,
	buttonConfig,
	cardVariant = "default",
	carouselImageSize = "default",
	carouselLayout = "default",
	centerFocusZoom = false,
	showcaseTextColor,
	showcaseButtonStyle,
	showcaseButtonConfig,
	showBrandLabel = true,
	brandLabelOverride,
	limitedEditionButtonConfig,
}: RelatedProductsCarouselProps) {
	const isGallery = carouselLayout === "gallery";
	const loopEnabled = !isGallery && slides.length > 1;
	const useCenterFocusZoom = centerFocusZoom && !isGallery;
	const galleryColumns = useGalleryColumns(isGallery);

	const startIndex = useMemo(() => {
		if (slides.length <= 1) return 0;

		if (isGallery) return 0;

		if (slides.length === 2) return 1;
		return Math.floor(slides.length / 2);
	}, [slides.length, isGallery]);

	// Gallery: center the single slide on mobile (1-up); start-align the
	// first product once there are multiple columns (desktop).
	const galleryMultiColumn = isGallery && galleryColumns > 1;

	const options = useMemo(
		() => ({
			align: galleryMultiColumn ? ("start" as const) : ("center" as const),
			loop: loopEnabled,
			startIndex,
			containScroll: galleryMultiColumn
				? ("trimSnaps" as const)
				: (false as const),
			slidesToScroll: 1,
		}),
		[loopEnabled, startIndex, galleryMultiColumn]
	);

	const [emblaRef, emblaApi] = useEmblaCarousel(options);
	const [selectedIndex, setSelectedIndex] = useState(0);

	const onSlideSelect = useCallback(() => {
		if (!emblaApi) return;
		setSelectedIndex(emblaApi.selectedScrollSnap());
	}, [emblaApi]);

	useEffect(() => {
		if (!emblaApi || !useCenterFocusZoom) return;

		onSlideSelect();
		emblaApi.on("select", onSlideSelect).on("reInit", onSlideSelect);

		return () => {
			emblaApi.off("select", onSlideSelect);
			emblaApi.off("reInit", onSlideSelect);
		};
	}, [emblaApi, onSlideSelect, useCenterFocusZoom]);

	useEffect(() => {
		if (!emblaApi) return;
		emblaApi.reInit();
		if (slides.length > 1) {
			emblaApi.scrollTo(startIndex, false);
		}
		if (useCenterFocusZoom) {
			onSlideSelect();
		}
	}, [
		emblaApi,
		slides.length,
		startIndex,
		galleryColumns,
		useCenterFocusZoom,
		onSlideSelect,
	]);

	const {
		prevArrowVisible,
		nextArrowVisible,
		onPrevButtonClick,
		onNextButtonClick,
	} = usePrevNextButtons(emblaApi);

	const useCustomMarker = Boolean(data.addCustomNavigationMarkers);

	return (
		<div
			className={cn(
				styles.embla,
				carouselLayout === "gallery" && styles.emblaGallery,
				carouselImageSize === "related" && styles.emblaRelated,
				useCenterFocusZoom && styles.emblaCenterFocus
			)}
			style={navStyle as React.CSSProperties}
		>
			<div className={styles.emblaStage}>
				{showNav && (
					<div
						className={cn(
							styles.emblaArrowSlot,
							!prevArrowVisible && styles.emblaArrowSlotHidden
						)}
					>
						<PrevButton
							className={styles.emblaArrowSide}
							onClick={onPrevButtonClick}
							navColor={navColor}
							useCustomMarker={useCustomMarker}
							markerStyle={navStyle}
							tabIndex={prevArrowVisible ? 0 : -1}
							aria-hidden={!prevArrowVisible}
						/>
					</div>
				)}

				<div className={styles.emblaViewport} ref={emblaRef}>
					<div className={styles.emblaContainer}>
						{slides.map((entry, index) => {
							const isActive =
								useCenterFocusZoom && index === selectedIndex;

							return (
								<div className={styles.emblaSlide} key={entry.id ?? index}>
									<div
										className={cn(
											styles.emblaSlideCard,
											carouselImageSize === "related" &&
												styles.emblaSlideCardRelated,
											isActive && styles.emblaSlideCardActive
										)}
									>
										{cardVariant === "limitedEdition" ? (
											<LimitedEditionRelatedCard
												variant="carousel"
												entry={entry}
												textColor={showcaseTextColor}
												buttonConfig={limitedEditionButtonConfig}
											/>
										) : cardVariant === "showcase" ? (
											<ShowcaseProductCard
												variant="carousel"
												carouselImageSize={carouselImageSize}
												entry={entry}
												locale={locale}
												portfolioSlug={portfolioSlug}
												textColor={showcaseTextColor}
												buttonConfig={showcaseButtonConfig}
												buttonStyle={showcaseButtonStyle}
												showBrandLabel={showBrandLabel}
												brandLabelOverride={brandLabelOverride}
											/>
										) : (
											<ProductCard
												variant="carousel"
												carouselImageSize={carouselImageSize}
												buttonConfig={buttonConfig}
												entry={entry}
												locale={locale}
												portfolioSlug={portfolioSlug}
												data={data}
											/>
										)}
									</div>
								</div>
							);
						})}
					</div>
				</div>

				{showNav && (
					<div
						className={cn(
							styles.emblaArrowSlot,
							!nextArrowVisible && styles.emblaArrowSlotHidden
						)}
					>
						<NextButton
							className={styles.emblaArrowSide}
							onClick={onNextButtonClick}
							navColor={navColor}
							useCustomMarker={useCustomMarker}
							markerStyle={navStyle}
							tabIndex={nextArrowVisible ? 0 : -1}
							aria-hidden={!nextArrowVisible}
						/>
					</div>
				)}
			</div>

		</div>
	);
}
